import React, { useCallback, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ImageCarousel — manual-only image slider.
 *   - Left / Right arrow buttons
 *   - Bottom dot indicators (click to jump)
 *   - Touch swipe support (left/right)
 *   - Keyboard ← / → when carousel is focused
 *   - NO auto-slide
 *
 * Props:
 *   images   : string[]        — array of resolved image URLs
 *   alt      : string          — base alt text
 *   className: string          — wrapper class (aspect ratio, etc.)
 *   testid   : string          — root data-testid
 *   fallback : string          — placeholder image URL
 */
const DEFAULT_FALLBACK = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
const SWIPE_THRESHOLD = 40;

export default function ImageCarousel({
  images = [],
  alt = "image",
  className = "aspect-[16/10]",
  testid = "image-carousel",
  fallback = DEFAULT_FALLBACK,
}) {
  const list = images && images.length > 0 ? images : [fallback];
  const [idx, setIdx] = useState(0);
  const startX = useRef(null);
  const wrapRef = useRef(null);

  const clamp = (n) => (n + list.length) % list.length;
  const go = useCallback((n) => setIdx(clamp(n)), [list.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const prev = useCallback(() => go(idx - 1), [idx, go]);
  const next = useCallback(() => go(idx + 1), [idx, go]);

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD) (dx < 0 ? next : prev)();
    startX.current = null;
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };

  return (
    <div
      ref={wrapRef}
      tabIndex={0}
      onKeyDown={onKey}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`relative overflow-hidden rounded-2xl bg-neutral-100 select-none focus:outline-none focus:ring-2 focus:ring-primary/40 ${className}`}
      data-testid={testid}
    >
      {/* Slide track */}
      <div
        className="flex h-full w-full transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {list.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={`${alt} - ${i + 1}`}
            loading={i === 0 ? "eager" : "lazy"}
            draggable={false}
            className="w-full h-full flex-shrink-0 object-cover"
            onError={(e) => { e.currentTarget.src = fallback; }}
            data-testid={`${testid}-slide-${i}`}
          />
        ))}
      </div>

      {/* Arrows — only when more than 1 image */}
      {list.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous image"
            className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-white/90 hover:bg-white text-neutral-900 shadow-lg flex items-center justify-center transition-all active:scale-95"
            data-testid={`${testid}-prev`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next image"
            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-white/90 hover:bg-white text-neutral-900 shadow-lg flex items-center justify-center transition-all active:scale-95"
            data-testid={`${testid}-next`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Counter chip */}
          <span
            className="absolute top-3 right-3 bg-black/60 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm"
            data-testid={`${testid}-counter`}
          >
            {idx + 1} / {list.length}
          </span>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid={`${testid}-dots`}>
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to image ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-6" : "bg-white/60 hover:bg-white/80 w-1.5"}`}
                data-testid={`${testid}-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
